import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { getCurrency } from '@common/utils/currency.ts'
import { format } from 'date-fns'
import { useUnit } from 'effector-react'
import { useQueryClient } from 'react-query'

import ArrowIcon from '@/assets/icons/icon-chevron-down.svg'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs.tsx'
import { Tag } from '@/components/ui/tag.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import { TextLabel } from '@/components/ui/text-label.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { $account } from '@/models/account.ts'
import { loadCreatorsFx } from '@/models/creators.ts'
import { DiscountBadge } from '@/modules/campaigns/components/discount-badge.tsx'
import { useCampaigns } from '@/modules/campaigns/state/hooks.ts'
import type { Creator } from '@/modules/creators/types.ts'
import { useModifyLicenseContext } from '@/modules/licenses/components/modify-license-context.tsx'
import { handleError } from '@/utils/error.ts'
import { useDebouncedProperty } from '@/utils/useDebouncedProperty.ts'

import { useGetLicense } from '../../hooks/use-get-license.tsx'
import type { BillingPlan, PurchasePreviewInfo } from '../../types'
import { LicenseMode } from '../../types'
import { LicenseBenefitsPopover } from '../license-benefits-popover.tsx'

interface PurchaseLicenseDialog {
  open: boolean
  setOpen: (open: boolean) => void
  clickedPlan: BillingPlan
  defaultToggle: LicenseMode
  creator?: Creator | undefined
  hideToggle?: boolean
}

interface PurchaseLicenseProps extends PurchaseLicenseDialog {
  hideHeader?: boolean
}

export const PurchaseLicense = (props: PurchaseLicenseProps) => {
  const account = useUnit($account)
  const {
    clickedPlan,
    defaultToggle,
    creator,
    open,
    setOpen,
    hideHeader,
    hideToggle,
  } = props

  const queryClient = useQueryClient()
  const { plans } = useModifyLicenseContext()
  const toast = useToast()
  const [selectedPlan, setSelectedPlan] = useState<BillingPlan>(clickedPlan)
  const [licenseQuantity, setLicenseQuantity] = useState(1)
  const [currentToggle, setCurrentToggle] = useState<string>(defaultToggle)
  const { currentDiscount } = useCampaigns()

  const {
    isFetching,
    purchasePreview,
    purchasePreviewIsError,
    purchasePreviewError,
    purchaseContext,
    purchaseMutation,
  } = useGetLicense(creator, selectedPlan, currentToggle, open, licenseQuantity)

  useEffect(() => {
    if (!open) {
      return
    }
    setSelectedPlan(clickedPlan)
  }, [open, clickedPlan])

  useEffect(() => {
    if (purchaseMutation.isError) {
      handleError('There was an error with purchasing this license.')
      setOpen(false)
    }
  }, [purchaseMutation, setOpen])

  useEffect(() => {
    if (purchasePreviewIsError) {
      handleError(
        purchasePreviewError,
        'There was an error with loading license data.',
      )
    }
  }, [
    purchaseMutation,
    purchasePreviewIsError,
    purchasePreviewError,
    setOpen,
    toast,
  ])

  const handleToggleChange = (value: string) => setCurrentToggle(value)

  const handleChange = (licenseName: string) => {
    const plan = plans.find(({ name }) => name === licenseName)

    if (plan) {
      setSelectedPlan(plan)
    }
  }

  const handleSubmit = () => {
    if (!purchaseContext) return
    purchaseMutation
      .mutateAsync(purchaseContext)
      .then((response) => {
        if (response.paymentSuccessful) {
          toast.success('Your new license is ready for use')
        } else {
          toast.error(
            'Your payment failed. Please pay the invoice to continue.',
          )
          queryClient.invalidateQueries(['owner', account?.userId])
        }
      })
      .catch((error) => {
        handleError(error, 'There was an error with purchasing this license.')
      })
      .finally(() => {
        setOpen(false)
        loadCreatorsFx()
        purchaseMutation.reset()
        queryClient.invalidateQueries('licenses')
        queryClient.invalidateQueries('creator')
      })
  }

  return (
    <>
      {!hideHeader && (
        <Dialog.Header>
          <Dialog.Title className="mb-2">
            {selectedPlan.displayName} License{' '}
            {creator != null ? ' for ' + creator.name : ''}
          </Dialog.Title>
        </Dialog.Header>
      )}
      <Dialog.Description className="font-medium">
        <Tabs
          defaultValue={currentToggle}
          onValueChange={handleToggleChange}
          className={`flex w-full flex-col justify-center ${
            hideHeader ? '' : 'mt-3'
          }`}
        >
          {!hideToggle && (
            <TabsList className="grid w-[400px] grid-cols-2 self-center">
              <TabsTrigger value={LicenseMode.MONTHLY}>Monthly</TabsTrigger>
              <TabsTrigger
                value={LicenseMode.YEARLY}
                className="gap-2"
              >
                Yearly
                <Tag className="h-fit bg-primary-400 text-white dark:bg-primary-500">
                  -25%
                </Tag>
              </TabsTrigger>
            </TabsList>
          )}

          <TabsContent
            value={LicenseMode.MONTHLY}
            className="w-full"
          >
            <GetLicenseForm
              onChange={handleChange}
              licenseMode={LicenseMode.MONTHLY}
              selectedLicense={selectedPlan}
              plans={plans}
              purchasePreview={purchasePreview}
              quantity={licenseQuantity}
              onQuantityChange={setLicenseQuantity}
              isFetching={isFetching}
              discount={currentDiscount}
            />
          </TabsContent>
          <TabsContent value={LicenseMode.YEARLY}>
            <GetLicenseForm
              onChange={handleChange}
              licenseMode={LicenseMode.YEARLY}
              selectedLicense={selectedPlan}
              plans={plans}
              purchasePreview={purchasePreview}
              quantity={licenseQuantity}
              onQuantityChange={setLicenseQuantity}
              isFetching={isFetching}
            />
          </TabsContent>
        </Tabs>
      </Dialog.Description>
      <Dialog.ClosableFooter onClose={() => setOpen(false)}>
        <Button
          onClick={handleSubmit}
          isLoading={purchaseMutation.isLoading}
        >
          Get License
        </Button>
      </Dialog.ClosableFooter>
    </>
  )
}

export const PurchaseLicenseDialog = (props: PurchaseLicenseDialog) => {
  const { open, setOpen } = props

  return (
    <Dialog
      show={open}
      onClose={() => setOpen(false)}
      bigDialog
    >
      <PurchaseLicense {...props} />
    </Dialog>
  )
}

function formatCalendarDate(date: string) {
  return format(new Date(date), 'dd MMM yyyy')
}

const currencyPrecision = 2

interface GetLicenseFormProps {
  purchasePreview: PurchasePreviewInfo | undefined
  licenseMode: string
  selectedLicense: BillingPlan
  onChange: (licenseName: string) => void
  plans: BillingPlan[]
  quantity: number
  onQuantityChange: Dispatch<SetStateAction<number>>
  isFetching?: boolean
  discount?: number
}

const GetLicenseForm = (props: GetLicenseFormProps) => {
  const {
    purchasePreview,
    licenseMode,
    selectedLicense,
    quantity,
    onQuantityChange,
    isFetching,
    discount,
  } = props
  const [debouncedQuantity, setDebouncedQuantity] = useDebouncedProperty<
    number | undefined
  >(quantity, handleChange)

  function increment() {
    if (quantity >= 100) {
      return
    }
    onQuantityChange(quantity + 1)
  }

  function decrement() {
    if (quantity <= 1) {
      return
    }

    onQuantityChange(quantity - 1)
  }

  function handleChange(value: number | undefined) {
    if (value === undefined || isNaN(value)) {
      value = 1
      setDebouncedQuantity(1)
    }

    if (value > 100) {
      return
    }

    const amount = value < 1 ? 1 : value

    onQuantityChange(amount)
  }

  const renderAmountWithDiscount = useCallback(
    (amount: string, amountAfterDiscount: string) => {
      if (!discount || amount === amountAfterDiscount) {
        return amount
      }

      return (
        <div className="flex gap-2">
          <span className="text-danger-default">{amountAfterDiscount}</span>
          <span className="line-through">{amount}</span>
        </div>
      )
    },
    [discount],
  )

  const {
    currentPayment,
    currentPaymentAfterDiscount,
    balance,
    payment,
    nextPayment,
  } = useMemo(() => {
    const amounts = {
      currentPayment: purchasePreview?.planPrice.price ?? 0,
      currentPaymentAfterDiscount:
        (purchasePreview?.dueNowIncludingTax ?? 0) / 100,
      balance: (purchasePreview?.customerBalance ?? 0) / 100,
      payment: (purchasePreview?.paymentAmount ?? 0) / 100,
      nextPayment: (purchasePreview?.planPrice?.price ?? 0) * quantity,
    }

    return Object.entries(amounts).reduce(
      (acc, [key, value]) => {
        acc[key as keyof typeof amounts] = getCurrency(value, currencyPrecision)

        return acc
      },
      {} as Record<keyof typeof amounts, string>,
    )
  }, [purchasePreview, quantity])

  return (
    <div>
      <div className="grid gap-3">
        <TextLabel className="text-title">Selected License</TextLabel>
        <div className="flex">
          <TextField
            className="max-w-[5rem] rounded-e-none"
            type="number"
            value={debouncedQuantity}
            max={100}
            onChange={(event) => {
              const value = event.target.value

              setDebouncedQuantity(value ? Number(value) : undefined)
            }}
            prefix={
              <div className="flex flex-col">
                <ArrowIcon
                  type="button"
                  className="mb-[-5px] rotate-180 cursor-pointer hover:opacity-50 dark:text-white"
                  onClick={increment}
                />

                <ArrowIcon
                  type="button"
                  className="cursor-pointer hover:opacity-50 dark:text-white"
                  onClick={decrement}
                />
              </div>
            }
          />
          <TextField
            className="flex rounded-s-none border-s-0"
            value={`${selectedLicense.displayName} License`}
            readOnly
          />
          <LicenseBenefitsPopover
            license={selectedLicense}
            className="right-5"
          />
        </div>
      </div>
      <div className="divide-y-[1px] divide-border">
        <div className="grid gap-4 py-6">
          <TextLabel className="text-title">License Costs</TextLabel>
          {isFetching || !purchasePreview ? (
            <Skeleton className="h-6 w-full dark:bg-background" />
          ) : (
            <div className="flex justify-between gap-3">
              <p className="text-sm">
                <span>
                  {quantity}x {selectedLicense.displayName} License (
                  {formatCalendarDate(purchasePreview.dueNowPeriodStart)} -{' '}
                  {formatCalendarDate(purchasePreview.dueNowPeriodEnd)})
                </span>{' '}
                {!!discount && (
                  <DiscountBadge className="inline-block px-2 py-0.5" />
                )}
              </p>
              <p className="text-right">
                {renderAmountWithDiscount(
                  currentPayment,
                  currentPaymentAfterDiscount,
                )}
              </p>
            </div>
          )}

          {!isFetching &&
            purchasePreview &&
            purchasePreview.customerBalance !== 0 && (
              <div className="flex justify-between">
                <p className="text-sm">Your balance</p>
                <p className="text-right">{balance}</p>
              </div>
            )}
        </div>
        <div className="flex items-center justify-between py-6">
          <div className="grid gap-1">
            <TextLabel className="text-title">Total cost today</TextLabel>
            <p className="text-sm">The amount you will be charged today</p>
          </div>
          <p className="text-right">
            {isFetching || !purchasePreview ? (
              <Skeleton className="h-6 w-24 dark:bg-background" />
            ) : (
              payment
            )}
          </p>
        </div>

        <div className="flex items-center justify-between py-6">
          {isFetching || !purchasePreview ? (
            <Skeleton className="h-10 w-full dark:bg-background" />
          ) : (
            <>
              <div className="grid gap-1">
                <TextLabel className="text-title">Total future cost</TextLabel>
                <p className="text-sm">
                  The amount you will be charged {licenseMode} from{' '}
                  {formatCalendarDate(purchasePreview.dueNowPeriodEnd)}
                </p>
              </div>
              <p className="text-right">
                {nextPayment} /{' '}
                {licenseMode.substring(0, licenseMode.length - 2)}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
